import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Folder, ChatCircle, File,
} from '@phosphor-icons/react';
import { Select, Spin } from 'antd';
import Typography from '../../components/Typography';
import { hasFeatureInLocalStorage } from '../../global/featureFlags';
import Button from '../../components/Button';
import StatsDashboard from '../../components/portal/StatsDashboard';
import ReferralsTable from '../../components/portal/ReferralsTable';
import ReferralFilters from './ReferralFilters';
import ReferralDetail from './ReferralDetailLoader';
import DocumentsPage from './DocumentsPage';
import MessagesPage from './MessagesPage';
import PortalTabs from './PortalTabs';
import usePortalNotifications from '../../hooks/usePortalNotifications';
import './referralsList.scss';

// Using Ant Design Select directly since our Select wrapper has different API

const DEFAULT_STATS = {
  total: 0,
  pending: 0,
  scheduled: 0,
  completed: 0,
  status_counts: {},
};

const PER_PAGE_OPTIONS = [
  { value: 10, label: '10 / page' },
  { value: 20, label: '20 / page' },
  { value: 50, label: '50 / page' },
];

const DEFAULT_FILTERS = {
  patient: '',
  provider: '',
  status: [],
  dob: '',
  date_from: '',
  date_to: '',
};


const getShareTokenFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const shareToken = params.get('share_token');
  if (!shareToken) return null;

  return shareToken;
};

const ReferralsList = ({ history, location, cable }) => {
  const deepLinkedShareToken = getShareTokenFromSearch(location.search);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState(null);
  const [activeTab, setActiveTab] = useState('referrals');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeStatFilter, setActiveStatFilter] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 20,
  });
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [lastNotificationAt, setLastNotificationAt] = useState(null);

  const handleDocumentUploaded = useCallback((data) => {
    // If the user is viewing the referral that was updated, trigger a refresh
    if (selectedReferral?.slug === data.referral_entity_slug) {
      setLastNotificationAt(Date.now());
    }
  }, [selectedReferral]);

  usePortalNotifications(handleDocumentUploaded);

  // Check feature flag access and portal eligibility
  useEffect(() => {
    const hasAccess = hasFeatureInLocalStorage('referral_portal');
    if (!hasAccess) {
      history.replace('/app');
      return;
    }

    // Portal-only orgs (no_features + referral_portal) always have access
    const isPortalOnly = hasFeatureInLocalStorage('no_features');
    if (isPortalOnly) return;

    // Regular orgs: check if they have inbound referral shares
    let active = true;
    axios.get('/api/v1/portal/referrals/has_portal_access')
      .then(({ data }) => {
        if (active && !data.has_portal_access) history.replace('/app/referred');
      })
      .catch(() => {
        // On error, allow access rather than blocking.
      });
    return () => { active = false; };
  }, [history]);

  useEffect(() => {
    if (activeTab !== 'referrals' && activeTab !== 'messages') return;

    let cancelled = false;

    const fetchReferrals = async () => {
      setLoading(true);
      setError('');

      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.perPage,
        };

        if (availableStatuses.length === 0) {
          params.include_statuses = 'true';
        }

        if (deepLinkedShareToken) {
          params.share_token = deepLinkedShareToken;
        }

        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            if (value.length > 0) params[key] = value.join(',');
          } else if (value) {
            params[key] = value;
          }
        });

        const response = await axios.get('/api/v1/portal/referrals', { params });
        const payload = response.data || {};
        const nextReferrals = payload.referrals || [];
        const nextStats = payload.stats || DEFAULT_STATS;
        const nextPagination = payload.pagination || {};

        if (cancelled) return;

        setReferrals(nextReferrals);
        if (payload.available_statuses) {
          setAvailableStatuses(payload.available_statuses);
        }
        setStats({
          total: nextStats.total || 0,
          pending: nextStats.pending || 0,
          scheduled: nextStats.scheduled || 0,
          completed: nextStats.completed || 0,
          status_counts: nextStats.status_counts || {},
        });

        setPagination((prev) => ({
          ...prev,
          currentPage: nextPagination.current_page || prev.currentPage,
          totalPages: nextPagination.total_pages || 1,
          totalCount: nextPagination.total_count || 0,
          perPage: nextPagination.per_page || prev.perPage,
        }));

        setSelectedReferral((prev) => {
          if (deepLinkedShareToken) {
            const deepLinkedReferral = nextReferrals.find((referral) => referral.share_token === deepLinkedShareToken);
            if (deepLinkedReferral) {
              return deepLinkedReferral;
            }
          }

          if (!prev) return null;
          return nextReferrals.find((referral) => referral.id === prev.id) || null;
        });
      } catch (fetchError) {
        if (cancelled) return;

        setReferrals([]);
        setStats(DEFAULT_STATS);
        setError(fetchError.response?.data?.error || 'Failed to load referrals. Please try again.');
        setPagination((prev) => ({
          ...prev,
          totalPages: 1,
          totalCount: 0,
        }));
        setSelectedReferral(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReferrals();

    return () => {
      cancelled = true;
    };
  }, [activeTab, deepLinkedShareToken, filters, pagination.currentPage, pagination.perPage]);

  const unreadMessagesCount = referrals.reduce(
    (sum, referral) => sum + (referral.unread_messages_count || 0),
    0,
  );

  const tabs = [
    { key: 'referrals', label: 'My Referrals', icon: <Folder size={16} /> },
    {
      key: 'messages', label: 'Messages', icon: <ChatCircle size={16} />, ...(unreadMessagesCount > 0 && { count: unreadMessagesCount }),
    },
    { key: 'documents', label: 'Documents', icon: <File size={16} /> },
  ];

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Sync stat card highlight with status dropdown
    setActiveStatFilter(newFilters.status || []);
  };

  const handleStatFilterClick = (filterValue) => {
    // "Total" card (empty filterValue) clears all status filters
    if (!filterValue) {
      setActiveStatFilter([]);
      setFilters((prev) => ({ ...prev, status: [] }));
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }

    // Toggle: add or remove the status from the array
    setActiveStatFilter((prev) => {
      const next = prev.includes(filterValue)
        ? prev.filter((v) => v !== filterValue)
        : [...prev, filterValue];
      setFilters((prevFilters) => ({ ...prevFilters, status: next }));
      setPagination((prevPag) => ({ ...prevPag, currentPage: 1 }));
      return next;
    });
  };

  const handleReferralClick = (referral) => {
    setDetailInitialTab(null);
    setSelectedReferral(referral);
  };

  const handleQuickAction = (referral, tab) => {
    setDetailInitialTab(tab);
    setSelectedReferral(referral);
  };

  const handleCloseDetail = () => {
    setSelectedReferral(null);
    setDetailInitialTab(null);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setActiveStatFilter([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleNavigate = (direction) => {
    if (!selectedReferral) return;

    const currentIndex = referrals.findIndex((r) => r.id === selectedReferral.id);
    if (currentIndex === -1) return;

    if (direction === 'prev' && currentIndex > 0) {
      setSelectedReferral(referrals[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < referrals.length - 1) {
      setSelectedReferral(referrals[currentIndex + 1]);
    }
  };

  const getNavigationState = () => {
    if (!selectedReferral) return { hasPrevious: false, hasNext: false };

    const currentIndex = referrals.findIndex((r) => r.id === selectedReferral.id);
    return {
      hasPrevious: currentIndex > 0,
      hasNext: currentIndex < referrals.length - 1,
    };
  };

  const hasActiveFilters = Object.entries(filters).some(([, v]) => (Array.isArray(v) ? v.length > 0 : !!v)) || activeStatFilter.length > 0;

  const startItem = pagination.totalCount === 0
    ? 0
    : ((pagination.currentPage - 1) * pagination.perPage) + 1;
  const endItem = Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount);

  return (
    <div className="referrals-list">
      <PortalTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'referrals' && (
        <>
          <div className="referrals-list__header">
            <div className="referrals-list__header-title">
              <Typography variant="h1" size="large" bold>
                Referral Tracker
              </Typography>
              <Typography variant="text" size="small" className="referrals-list__subtitle">
                Monitor all your referrals in real time
              </Typography>
            </div>
            <Button
              type="primary"
              label="New Referral"
              leftIcon={<Plus size={16} weight="bold" />}
              className="referrals-list__new-btn"
            />
          </div>

          <StatsDashboard
            stats={stats}
            activeFilter={activeStatFilter}
            onFilterClick={handleStatFilterClick}
          />

          <ReferralFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            availableStatuses={availableStatuses}
          />

          {error && (
            <Typography variant="text" size="small" className="referrals-list__error">
              {error}
            </Typography>
          )}

          {loading ? (
            <div className="referrals-list__loading">
              <Spin />
            </div>
          ) : (
            <ReferralsTable
              referrals={referrals}
              onReferralClick={handleReferralClick}
              onQuickAction={handleQuickAction}
              selectedId={selectedReferral?.id}
              activeStatFilter={activeStatFilter}
              filters={filters}
              onClearFilters={handleClearFilters}
            />
          )}

          {referrals.length > 0 && (
            <div className="referrals-list__pagination">
              <Typography variant="text" size="small" className="referrals-list__pagination-info">
                Showing
                {' '}
                {startItem}
                –
                {endItem}
                {' '}
                of
                {' '}
                {pagination.totalCount}
                {hasActiveFilters && stats.total > pagination.totalCount && (
                  <span>
                    {' '}
                    (
                    {stats.total}
                    {' '}
                    total)
                  </span>
                )}
              </Typography>

              <div className="referrals-list__pagination-controls">
                <Select
                  value={pagination.perPage}
                  onChange={(value) => setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }))}
                  options={PER_PAGE_OPTIONS}
                  className="referrals-list__per-page"
                />

                <Typography variant="text" size="small" className="referrals-list__page-info">
                  Page
                  {' '}
                  {pagination.currentPage}
                  {' '}
                  of
                  {' '}
                  {pagination.totalPages || 1}
                </Typography>

                <div className="referrals-list__pagination-nav">
                  <Button
                    type="secondary"
                    size="small"
                    label="Previous"
                    disabled={pagination.currentPage === 1 || loading}
                    onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  />
                  <Button
                    type="secondary"
                    size="small"
                    label="Next"
                    disabled={pagination.currentPage >= pagination.totalPages || loading}
                    onClick={() => setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  />
                </div>
              </div>
            </div>
          )}

          <ReferralDetail
            referral={selectedReferral}
            onClose={handleCloseDetail}
            onNavigate={handleNavigate}
            hasPrevious={getNavigationState().hasPrevious}
            hasNext={getNavigationState().hasNext}
            lastNotificationAt={lastNotificationAt}
            cable={cable}
            initialTab={detailInitialTab}
            onMarkRead={(slug) => {
              setReferrals((prev) => prev.map((r) =>
                r.slug === slug ? { ...r, unread_messages_count: 0 } : r
              ));
            }}
          />
        </>
      )}

      {activeTab === 'messages' && (
        <MessagesPage
          referrals={referrals}
          cable={cable}
          onMarkRead={(slug) => {
            setReferrals((prev) => prev.map((r) =>
              r.slug === slug ? { ...r, unread_messages_count: 0 } : r
            ));
          }}
        />
      )}

      {activeTab === 'documents' && (
        <DocumentsPage />
      )}
    </div>
  );
};

ReferralsList.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  cable: PropTypes.object,
};

ReferralsList.defaultProps = {
  cable: null,
};

export default withRouter(ReferralsList);
